const POLL_INTERVAL_MS = 5000;
const TOTAL_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 5;
const CREEP_START = 12;
const CREEP_STEP = 6;
const CREEP_CAP = 90;

const SPINNER_HTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
const ICON_SUCCESS = '<i class="fal fa-check-circle"></i>';
const ICON_ERROR = '<i class="fal fa-exclamation-circle"></i>';
const ICON_WARNING = '<i class="fal fa-exclamation-triangle"></i>';

let activeToken = 0;

interface StatusBody {
  status: string;
  error_message?: string | null;
  facebook_campaign_id?: string | null;
}

interface ProgressUI {
  $wrap: JQuery;
  $fill: JQuery;
  $icon: JQuery;
  $label: JQuery;
  $percent: JQuery;
}

export function startCreateProgress(adCampaignId: string): void {
  const token = ++activeToken;
  const $wrap = $('#campaign-create-progress');
  if (!$wrap.length) return;

  const ui: ProgressUI = {
    $wrap,
    $fill: $wrap.find('[data-role="ccp-fill"]'),
    $icon: $wrap.find('[data-role="ccp-icon"]'),
    $label: $wrap.find('[data-role="ccp-label"]'),
    $percent: $wrap.find('[data-role="ccp-percent"]'),
  };

  $wrap.removeClass('d-none is-success is-error is-warning').addClass('is-active');
  ui.$fill.addClass('progress-bar-striped progress-bar-animated');
  ui.$icon.html(SPINNER_HTML);
  ui.$label.text('Processing on Meta…').removeAttr('title');
  setBar(ui, CREEP_START, true);

  $wrap.find('[data-role="ccp-dismiss"]').off('click.ccp').on('click.ccp', function() {
    activeToken++;
    hideProgress($wrap);
  });

  void runPoll(adCampaignId, token, ui);
}

async function runPoll(adCampaignId: string, token: number, ui: ProgressUI): Promise<void> {
  const deadline = Date.now() + TOTAL_TIMEOUT_MS;
  let consecutiveErrors = 0;
  let lastStatus: string | null = null;
  let creep = CREEP_START;

  while (Date.now() < deadline) {
    if (token !== activeToken || isDetached(ui.$wrap)) return;

    try {
      const r = await fetch(`/meta_apis/ad_campaigns/${adCampaignId}/status`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`status check failed: ${r.status}`);
      const body = await r.json() as StatusBody;
      if (token !== activeToken) return;

      consecutiveErrors = 0;
      lastStatus = body.status;

      if (body.status === 'created') {
        applyState(ui, 'is-success', ICON_SUCCESS);
        setBar(ui, 100, true);
        ui.$label.text('Campaign created on Meta').removeAttr('title');
        await delay(1200);
        if (token !== activeToken) return;
        window.location.href = '/meta_apis/ad_campaigns?refresh=1';
        return;
      }
      if (body.status === 'failed') {
        const msg = body.error_message || 'Campaign creation failed on Meta.';
        renderTerminal(ui, 'is-error', ICON_ERROR, `Failed: ${msg}`, msg);
        return;
      }

      ui.$label.text(body.status === 'pending' ? 'Queued…' : 'Processing on Meta…');
      creep = Math.min(CREEP_CAP, creep + CREEP_STEP);
      setBar(ui, creep, true);
    } catch {
      if (token !== activeToken) return;
      consecutiveErrors++;
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        const msg =
          `Lost connection while checking campaign status (${MAX_CONSECUTIVE_ERRORS} consecutive errors). ` +
          'The campaign may still be processing on Meta — refresh the campaign list to confirm.';
        renderTerminal(ui, 'is-warning', ICON_WARNING, 'Lost connection — refresh to confirm.', msg);
        return;
      }
    }

    await delay(POLL_INTERVAL_MS);
  }

  if (token !== activeToken) return;
  const stalledMessage = (lastStatus === 'processing' || lastStatus === 'pending')
    ? `Campaign has been "${lastStatus}" for over 5 minutes and may have stalled. ` +
      'Refresh the campaign list — if the campaign is missing, try creating it again.'
    : 'Campaign status check timed out after 5 minutes. Refresh the campaign list to see the outcome.';
  renderTerminal(ui, 'is-warning', ICON_WARNING, 'Timed out — refresh to see the outcome.', stalledMessage);
}

function renderTerminal(
  ui: ProgressUI,
  stateClass: 'is-error' | 'is-warning',
  iconHtml: string,
  shortText: string,
  fullText: string,
): void {
  applyState(ui, stateClass, iconHtml);
  setBar(ui, 100, false);
  ui.$label.text(shortText)
  $(".campaign-create-progress").attr("title", fullText);
}

function applyState(ui: ProgressUI, stateClass: string, iconHtml: string): void {
  ui.$wrap.removeClass('is-active is-success is-error is-warning').addClass(stateClass);
  ui.$fill.removeClass('progress-bar-striped progress-bar-animated');
  ui.$icon.html(iconHtml);
}

function setBar(ui: ProgressUI, pct: number, showPercent: boolean): void {
  const v = Math.max(0, Math.min(100, Math.round(pct)));
  ui.$fill.css('width', `${v}%`).attr('aria-valuenow', String(v));
  ui.$percent.text(showPercent ? `${v}%` : '');
}

function hideProgress($wrap: JQuery): void {
  $wrap.addClass('d-none').removeClass('is-active is-success is-error is-warning');
}

function isDetached($wrap: JQuery): boolean {
  const el = $wrap.get(0);
  return !el || !document.body.contains(el);
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
